/**
 * №53 (53.0, D-CR14 / D-CR21) — SETTINGS IS A PAGE, and it is where the console goes.
 *
 * This is №35's payload arriving inside this rung. David, 2026-08-09: *"make the
 * console a settings page inside the main app"*, and again 2026-08-19: *"the '.app'
 * route will survive, and the '/console' will be completely removed."*
 *
 * ── Three groups, by WHO EACH THING BELONGS TO ───────────────────────────────
 *
 *   The firm       what it runs on          — rates, people, the trust ledger, the playbook
 *   The workspace  what it is built from    — connections, tables, workflows, endpoints, library
 *   You            this browser, this account
 *
 * Grouped by owner, deliberately, rather than by which application used to hold
 * each thing. A user does not care that Members was a console page and Appearance
 * was a shell toggle; they care whose setting it is, because that is what tells
 * them whether changing it affects their colleagues.
 *
 * **NOTHING ON THIS PAGE SAYS "CONSOLE".** That word is overloaded in this repo —
 * one directory, two surfaces — and it means nothing to a lawyer.
 *
 * ── Re-parenting, not rebuilding ─────────────────────────────────────────────
 *
 * Every row except Trust ledger is a live route today; Trust ledger is reachable
 * only from inside the ledger itself. So absorbing the console is mounting those
 * components under `/app/settings/*` and letting them render inside the shell —
 * which is what these routes do. The user stops leaving `/app` to administer the
 * firm, which is the whole point of deleting the rail's `Console` link.
 *
 * **What this rung does NOT do, stated so it is not mistaken for done:** the
 * re-skinning of what sits behind each row. Those pages still render their own
 * (maker-era) chrome inside the shell frame. 53.0 ships the page, the menu entry
 * and the re-parenting; the spec is explicit that the rest is later.
 *
 * ── Counts are real or absent ────────────────────────────────────────────────
 *
 * The mockup draws a figure beside most rows ("5 live", "21", "7 published").
 * Every one of those is a live count, and this page renders a count ONLY where it
 * already has the data in hand from a query the shell was making anyway. A row
 * whose figure would need its own round-trip renders no figure rather than a
 * zero — the standing rule, and the one the dial in 53.5 is judged on. A wrong
 * "0 connections" beside a firm that has five is worse than no number at all.
 */
import { NavLink } from "react-router";
import { useAuth } from "../../../lib/hooks/use-auth";
import { useV3Theme } from "../../hooks/use-v3-theme";
import { useWorkspaceInfo } from "../../../lib/hooks/use-kuvio";

interface Row {
  label: string;
  hint: string;
  /** Where the row goes. Every one is a route that resolves — dead-nav ban. */
  to: string;
  /** Live figure. `undefined` renders nothing; never a placeholder zero. */
  meta?: string;
}

function Group({ title, eyebrow, rows }: { title: string; eyebrow: string; rows: Row[] }) {
  return (
    <section className="setgroup">
      <header className="setgroup__head">
        <h2 className="setgroup__title">{title}</h2>
        <span className="setgroup__eyebrow">{eyebrow}</span>
      </header>
      {rows.map((r) => (
        <NavLink key={r.to} to={r.to} className="setrow">
          <span className="setrow__main">
            <span className="setrow__label">{r.label}</span>
            <span className="setrow__hint">{r.hint}</span>
          </span>
          <span className="setrow__meta">{r.meta ?? "→"}</span>
        </NavLink>
      ))}
    </section>
  );
}

export function SettingsPage() {
  const { user, currentWorkspaceId, logout } = useAuth();
  const { theme, toggle } = useV3Theme();
  const wsInfo = useWorkspaceInfo();

  const workspace = user?.workspaces.find((w) => w.id === currentWorkspaceId);
  const isOwner = workspace?.role === "owner";

  // The one count already in hand: `get_workspace_info` is fetched by the shell
  // for the brand and the library root, so the Library figure costs nothing extra.
  // Everything else would need its own call and therefore shows no figure.
  const libraryCount = wsInfo.data?.library_root?.length;

  const firm: Row[] = [
    { label: "Firm settings", hint: "Rates, numbering, matter types", to: "/app/settings/firm" },
    { label: "People & roles", hint: "Who is here and what they can reach", to: "/app/settings/members" },
    // Trust ledger is NOT here, and the mockup already knew why: it is *"the one
    // addition — currently reachable only from inside the ledger itself."* Every
    // ledger route takes a `:ledgerId` (`workbench-registry.ts:41`), so a row here
    // would need a `list_ledgers` round-trip this page does not otherwise make, and
    // a firm with no ledger enabled would get a row to nowhere. The dead-nav ban
    // says leave it out and say so. It returns when the page has the id — a small
    // piece of work, deliberately not smuggled into a chrome rung.
    { label: "Playbook & learnings", hint: "What the crew has been taught", to: "/app/settings/library" },
  ];

  const workspaceRows: Row[] = [
    { label: "Connections & equipment", hint: "What the crew can reach, and how", to: "/app/settings/connections" },
    { label: "Tables & types", hint: "The records this firm keeps", to: "/app/settings/tables" },
    { label: "Workflows", hint: "How matters are carried", to: "/app/settings/workflows" },
    { label: "Endpoints", hint: "Where hosts connect in", to: "/app/settings/endpoints" },
    { label: "Workspace", hint: "Name, theme, protection", to: "/app/settings/workspace" },
    {
      label: "Library",
      hint: "Pages, memories, prompts",
      to: "/app/settings/library",
      ...(libraryCount != null ? { meta: String(libraryCount) } : {}),
    },
  ];

  return (
    <div className="settingspage">
      <header className="settingspage__head">
        <h1>Settings</h1>
        <p>
          {workspace?.name ?? "This workspace"}
          {isOwner ? " · you are an owner here, so you can see all three." : " · some firm settings are owner-only."}
        </p>
      </header>

      <div className="settingspage__cols">
        <Group title="The firm" eyebrow="what it runs on" rows={firm} />
        <Group title="The workspace" eyebrow="what it is built from" rows={workspaceRows} />

        {/* `You` is not a list of routes — three of its four rows act in place.
            Rendered as its own markup rather than forced through `Group`, because
            a row that toggles a theme is not a row that navigates, and dressing
            one as the other is how a surface starts lying about what it does. */}
        <section className="setgroup">
          <header className="setgroup__head">
            <h2 className="setgroup__title">You</h2>
            <span className="setgroup__eyebrow">this browser, this account</span>
          </header>
          {/* Appearance ACTS, it does not navigate — there is no
              `/app/settings/appearance` surface and inventing one to hold a single
              toggle would be a page for a switch. */}
          <button className="setrow setrow--action" onClick={toggle}>
            <span className="setrow__main">
              <span className="setrow__label">Appearance</span>
              <span className="setrow__hint">Light or dark</span>
            </span>
            <span className="setrow__meta">{theme}</span>
          </button>
          <div className="setrow setrow--static">
            <span className="setrow__main">
              <span className="setrow__label">Keyboard shortcuts</span>
              <span className="setrow__hint">Search, jump, answer a card</span>
            </span>
            <span className="setrow__meta">⌘K</span>
          </div>
          {/* Time display is drawn in the mockup and is NOT built: the billing
              increment is hardcoded (`format-units.ts:23`) and making it a resolved
              setting is inside №54, which has not run. A row here would be a
              control over a value it cannot change — the dead-nav ban's neighbour —
              so it is left out and recorded rather than shipped inert. */}
          <button className="setrow setrow--action" onClick={() => void logout()}>
            <span className="setrow__main">
              <span className="setrow__label">Sign out</span>
              <span className="setrow__hint">{user?.email ?? ""}</span>
            </span>
            <span className="setrow__meta">→</span>
          </button>
        </section>
      </div>
    </div>
  );
}
